<template>
  <div class="c-domain-setup-form">
    <el-form
        ref="form"
        :model="domainSetupForm"
        label-position="top"
        label-width="120px"
        @submit.native.prevent>
      <el-row :gutter="20">
        <el-col :span="11">
          <el-form-item label="Domain">
            <el-select
                v-model="currentDomainID"
                class="w-full gray"
                placeholder="Choose a domain"
                default-first-option
                filterable>
              <el-option
                  v-for="domain in allDomains"
                  :key="domain.id"
                  :label="domain.url"
                  :value="domain.id"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12" :offset="1" class="target-settings mb-4">
          <el-form-item label="Show on specific pages">
            <el-switch v-model="domainSetupForm.show_on_specific_pages"></el-switch>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="12" :offset="12">
          <el-form-item v-if="domainSetupForm.show_on_specific_pages">
            <div class="flex flex-col items-start gap-y-2">
              <div class="w-full flex gap-x-2">
                <el-input
                    ref="saveTagInput"
                    v-model="inputValue"
                    class="grow gray"
                    placeholder="/subdirectory"
                    @keyup.enter.native="handleInputConfirm"
                    @blur="handleInputConfirm"/>
                <el-button
                    class="button-new-url"
                    size="medium"
                    @click="handleInputConfirm">
                  Add
                </el-button>
              </div>
              <!-- Validation error -->
              <div v-if="pathValidationError" class="text-onvocado-red text-xs">
                {{ pathValidationError }}
              </div>

              <el-tag
                  v-for="(subdir, index) in alphabeticallySortedPages"
                  :key="index"
                  :disable-transitions="false"
                  type="info"
                  closable
                  @close="handleClose(subdir.path)">
                /{{ subdir.path }}
              </el-tag>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
export default {
  name: 'DomainSetupForm',
  data() {
    return {
      enableUpdate: false,
      inputValue: '',
      pathValidationError: '',
      domainSetupForm: {
        domain: '',
        domainID: '',
        show_on_specific_pages: false,
        subdirectories: [],
      },
    }
  },
  computed: {
    allDomains() {
      return this.$store.state.domain.domains
    },
    campaign() {
      return this.$store.getters['campaign/targetCampaign']
    },
    currentDomainID: {
      get() {
        return this.domainSetupForm.domainID
      },
      set(domainID) {
        const domainObj = this.allDomains.find(domain => domain.id === domainID)

        if (domainObj) {
          this.domainSetupForm.domain = domainObj.url
          this.domainSetupForm.domainID = domainObj.id
        } else {
          console.error('Selected domain ID does not exist in allDomains')
        }
      },
    },
    alphabeticallySortedPages() {
      return this.domainSetupForm.subdirectories
          ? this.domainSetupForm.subdirectories.slice().sort((a, b) => a.path.localeCompare(b.path))
          : []
    },
  },
  watch: {
    'campaign.domain'(newVal) {
      if (this.domainSetupForm.domain !== newVal) {
        this.domainSetupForm.domain = newVal
      }
    },
    'campaign.domainID'(newVal) {
      if (this.domainSetupForm.domainID !== newVal) {
        this.domainSetupForm.domainID = newVal
      }
    },
    'campaign.show_on_specific_pages'(newVal) {
      if (this.domainSetupForm.show_on_specific_pages !== newVal) {
        this.domainSetupForm.show_on_specific_pages = newVal
      }
    },
    'campaign.subdirectories'(newVal) {
      if (JSON.stringify(this.domainSetupForm.subdirectories) !== JSON.stringify(newVal)) {
        this.domainSetupForm.subdirectories = newVal
      }
    },
    domainSetupForm: {
      handler() {
        if (this.enableUpdate) {
          this.$emit('update', this.domainSetupForm)
        }
      },
      deep: true,
      immediate: false,
    },
  },
  created() {
    this.setFormValues()
    this.$nextTick(() => {
      this.enableUpdate = true
    })
  },
  methods: {
    setFormValues() {
      const newValue = JSON.parse(JSON.stringify(this.campaign))
      this.domainSetupForm.domain = newValue.domain
      this.domainSetupForm.domainID = newValue.domainID
      this.domainSetupForm.show_on_specific_pages = newValue.show_on_specific_pages
      // Ensure subdirectories are objects with 'path' keys
      this.domainSetupForm.subdirectories = newValue.subdirectories.map(subdir => ({
        path: subdir.path,
      }))
    },
    handleClose(tagPath) {
      const index = this.domainSetupForm.subdirectories.findIndex(subdir => subdir.path === tagPath)
      if (index !== -1) {
        this.domainSetupForm.subdirectories.splice(index, 1)
      }
    },
    isValidPath(value) {
      // Disallow spaces and slashes
      if (value.includes(' ')) {
        this.pathValidationError = 'Page cannot contain spaces.'
        return false
      }
      // Allow only letters, numbers, underscores, and hyphens
      const regex = /^[a-zA-Z0-9_\\/-]+$/
      if (!regex.test(value)) {
        this.pathValidationError = 'Invalid page. Only letters, numbers, underscores, hyphens, and slashes are allowed.'
        return false
      }
      // Clear any previous error
      this.pathValidationError = ''
      return true
    },

    handleInputConfirm() {
      let inputValue = this.inputValue.trim()
      if (inputValue) {
        // Remove leading and trailing slashes
        inputValue = inputValue.replace(/^\/+|\/+$/g, '')

        // Check if the inputValue is empty after trimming slashes
        if (!inputValue) {
          this.pathValidationError = 'Page cannot be empty.'
          return
        }
        // Validate the input value
        if (!this.isValidPath(inputValue)) {
          return
        }
        // Prevent duplicate subdirectories
        if (!this.domainSetupForm.subdirectories.some(subdir => subdir.path === inputValue)) {
          this.domainSetupForm.subdirectories.push({ path: inputValue })
        }
      }
      this.inputValue = ''
    },
  },
}
</script>

<style lang="scss">
.c-domain-setup-form {

  .el-form-item {
    width: 100%;
  }

  .el-switch__label span {
    margin-bottom: 10px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1em;
  }

  .target-settings {
    .el-tag {
      display: block;
      margin: 5px 0;
    }

    .el-button {
      padding: 11px 20px;
    }
  }
}
</style>
